import React, { useEffect, useRef, useState } from 'react';
import { Participation, useMarkAsNotInterested } from '@app/api';
import { CardHomeBase, StyleCardHome } from './CardHomeBase';
import { IconsMeetingList } from './IconsMeetingList';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Button,
  useParticipantUnreadMessageByParticipation,
  useToast,
} from '@app/core';
import { Link } from 'react-router-dom';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h5,
    fontSize: 14,
  },
  descriptionContainer: {
    paddingTop: theme.marginBase * 2,
    overflow: 'hidden',
  },
  description: {
    ...theme.fonts.caption,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineClamp: 3,
    display: '-webkit-box',
    boxOrient: 'vertical',
  },
  viewMore: {
    ...theme.fonts.label,
    paddingTop: theme.marginBase / 2,
    color: theme.colors.blueAccessible,
    cursor: 'pointer',
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
}));

interface Props {
  participation: Participation;
  className: string;
}

export const AppliedParticipationCard = ({
  className,
  participation,
}: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { t } = useTranslation();
  const { mutateAsync: markAsNotInterested, isLoading } =
    useMarkAsNotInterested(participation.id);

  const numberOfNewMessages: number =
    useParticipantUnreadMessageByParticipation(
      participation.participantId,
      participation.id,
    );

  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const ref = useRef<any>(null);

  useEffect(() => {
    const clientHeight = ref.current.clientHeight;
    const scrollHeight = ref.current.scrollHeight;
    if (!scrollHeight || !clientHeight) {
      setIsTruncated(false);
    }
    setIsTruncated(clientHeight < scrollHeight);
  }, []);

  if (!participation.session) return null;

  return (
    <CardHomeBase
      style={StyleCardHome.WHITE}
      title={t('home.applied.title')}
      className={className}
    >
      <Link to={`/p/${participation.id}`} state={{ allowBack: true }}>
        <h1 className={classes.title}>{participation.session.title}</h1>
        {participation.session.description && (
          <div className={classes.descriptionContainer}>
            <h5
              className={classes.description}
              id={`${participation.id}ViewMoreMatched`}
              ref={ref}
            >
              {participation.session.description}
            </h5>
            {isTruncated && (
              <span className={classes.viewMore}>
                {t('home.applyStudies.viewMore')}
              </span>
            )}
          </div>
        )}
      </Link>
      <IconsMeetingList
        session={participation.session}
        focus={false}
        participation={participation}
        numberOfNewMessages={numberOfNewMessages}
      />
      <div className={classes.buttonContainer}>
        <Button
          color="red"
          variant="transparent"
          onClick={async () => {
            try {
              await markAsNotInterested();
              toast.saved();
            } catch (e) {
              toast.exception(e);
            }
          }}
          disabled={isLoading}
        >
          {t('home.applied.notInterested')}
        </Button>
      </div>
    </CardHomeBase>
  );
};
